/**
 * O corpo de `POST /companies` a partir do que o wizard coletou.
 *
 * Função pura, e num arquivo próprio, porque é a peça que perdia dado:
 * até 2026-08-30 o `CompanyOnboarding` mandava o payload do backend
 * DESCARTADO (`primary_brand`, `legal_name`, `tax_id`, `country_code`,
 * `timezone`, `logo`). O Pydantic ignora campo extra em silêncio, então
 * a empresa nascia só com o NOME e tudo o mais que a pessoa digitou —
 * ou que o autofill sugeriu — era jogado fora sem aviso.
 *
 * Separada do componente pra poder ser testada sem montar um wizard de
 * quatro passos. O teste que ela habilita é o que impede a volta do
 * bug, e ele não existia porque a lógica morava dentro de um `onClick`.
 *
 * **O mapeamento das listas pras categorias é o que
 * `web_api/application/companies.py` documenta**, e as categorias são
 * fechadas lá (`CITATION_TAG_CATEGORIES`): o servidor recusa categoria
 * desconhecida com 422. Mudar um rótulo aqui sem mudar lá quebra o
 * cadastro inteiro, não só uma lista.
 */

import type { CreateCompanyPayload } from '@/shared/api/api-client';

/** Categoria de citation tag → o campo do wizard que a alimenta. */
export const CITATION_TAG_CATEGORIES = {
  APELIDOS_E_NOMES_COMUNS: 'APELIDOS E NOMES COMUNS',
  SLOGANS: 'SLOGANS',
  PRODUTOS: 'PRODUTOS',
  PROGRAMAS_E_INICIATIVAS: 'PROGRAMAS E INICIATIVAS',
  CAMPANHAS_PUBLICITARIAS: 'CAMPANHAS PUBLICITARIAS'
} as const;

export type CompanyWizardFields = {
  name: string;
  /** Vazio = a marca principal herda o nome da empresa (o caso comum). */
  brandName: string;
  aliases: string;
  commonNames: string;
  slogans: string;
  products: string;
  initiatives: string;
  campaigns: string;
};

/**
 * Quebra uma lista digitada em vírgula, ponto-e-vírgula ou quebra de
 * linha — as três formas que as pessoas usam, e as três que o autofill
 * pode devolver.
 */
export function splitList(cru: string): string[] {
  return cru
    .split(/[,;\n]/)
    .map(item => item.trim())
    .filter(Boolean);
}

export function buildCreateCompanyPayload(
  fields: CompanyWizardFields
): CreateCompanyPayload {
  const tags: Record<string, string[]> = {};

  // Apelidos e nomes comuns caem na MESMA categoria — é uma categoria
  // legada só, e separá-las aqui inventaria uma que o servidor recusa.
  const apelidos = [
    ...splitList(fields.aliases),
    ...splitList(fields.commonNames)
  ];
  if (apelidos.length) {
    tags[CITATION_TAG_CATEGORIES.APELIDOS_E_NOMES_COMUNS] = apelidos;
  }

  const porCategoria: [string, string][] = [
    [CITATION_TAG_CATEGORIES.SLOGANS, fields.slogans],
    [CITATION_TAG_CATEGORIES.PRODUTOS, fields.products],
    [CITATION_TAG_CATEGORIES.PROGRAMAS_E_INICIATIVAS, fields.initiatives],
    [CITATION_TAG_CATEGORIES.CAMPANHAS_PUBLICITARIAS, fields.campaigns]
  ];
  for (const [categoria, cru] of porCategoria) {
    const valores = splitList(cru);
    if (valores.length) tags[categoria] = valores;
  }

  return {
    name: fields.name.trim(),
    // `undefined` e não string vazia: o servidor trata ausência como
    // "a marca herda o nome da empresa", e `''` viraria uma marca sem
    // nome.
    main_brand_name: fields.brandName.trim() || undefined,
    citation_tags: tags
  };
}
